import { GeneratePointI } from 'src/app/interfaces/map.interface';
import { Coords } from 'src/app/swagger-api';
import MapSettings from '../../map.settings';

export class DiametricalCoordsCreatorFactory {
  /**
   * @description Generates a list of random diametrically located to each other
   *              geolocation points around circle with given center and radius.
   * @param { GeneratePointI } content
   * @return { Coords[] }
   */
  public static createCoordsList(content: GeneratePointI): Coords[] {
    return [...Array(content.count).keys()].map((index: number) => this.pointInCircle(content, index + 1));
  }

  /**
   * @description Convert degrees to radians depend on incoming value.
   * @param { Coords } input
   * @param { Coords } zoom
   * @returns { Coords }
   */
  private static toRadians(input: Coords, zoom = 1): Coords {
    return this.recursiveConvert(input, (val: number) => val * MapSettings.DEG_TO_RAD * zoom) as Coords;
  }

  /**
   * @description Convert radians to degrees depend on incoming value.
   * @param { Coords } input
   * @returns { Coords }
   */
  private static toDegrees(input: Coords): Coords {
    return this.recursiveConvert(input, (val: number) => val / MapSettings.DEG_TO_RAD) as Coords;
  }

  /**
   * @description Recursive converter helper.
   * @param { Coords | number } input
   * @param { (value: number) => number } callback
   * @returns { Coords | number }
   */
  private static recursiveConvert(input: Coords | number, callback: (value: number) => number): number | Coords {
    if (input instanceof Object) {
      input = JSON.parse(JSON.stringify(input)) as Coords | number;
      for (const key in input as Coords) {
        if (Object.getOwnPropertyDescriptor(input, key)) {
          input[key] = this.recursiveConvert(input[key] as Coords | number, callback);
        }
      }
      return input;
    }

    if (!isNaN(input) && isFinite(input)) {
      return callback(input);
    }

    // if no conditions are met return default value
    return input;
  }

  /**
   * @description Generates a random geolocation point in circle with given
   *              center and radius using the specified .
   * @param { GeneratePointI } content
   * @param { number } index
   * @return { Coords }
   */
  private static pointInCircle(content: GeneratePointI, index: number): Coords {
    const radius: number = Math.pow(content.count, 0.5) * content.radius;
    const result: Coords = { lat: null, lon: null };
    const coordsInRadians: Coords = this.toRadians(content.center, 1);
    const sinLat: number = Math.sin(coordsInRadians.lat);
    const cosLat: number = Math.cos(coordsInRadians.lat);

    const angle: number = (index / content.count) * MapSettings.TWO_PI;
    const theta: number = radius / MapSettings.GOOGLE_MAPS_EARTH_MEAN_RADIUS_IN_METER;
    const sinBearing: number = Math.sin(angle);
    const cosBearing: number = Math.cos(angle);
    const sinTheta: number = Math.sin(theta);
    const cosTheta: number = Math.cos(theta);

    result.lat = Math.asin(sinLat * cosTheta + cosLat * sinTheta * cosBearing);
    result.lon =
      coordsInRadians.lon + Math.atan2(sinBearing * sinTheta * cosLat, cosTheta - sinLat * Math.sin(result.lat));

    /* Normalization: -PI -> +PI radians */
    result.lon = ((result.lon + MapSettings.THREE_PI) % MapSettings.TWO_PI) - Math.PI;

    return this.toDegrees(result);
  }
}
